/** @jsxImportSource @emotion/react */
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { sendCampaignTicketEmail } from "../../api/campaignApi";
import { downloadCampaignTicket } from "../../api/campaignApi";
import { UserContext } from "../../context/UserContext";
import {
  getCollection,
  useTicketHistory,
} from "../../hooks/useCampaignQueries";
import CampaignStatusBadge from "./components/CampaignStatusBadge";
import ReservationCountdown from "./components/ReservationCountdown";
import {
  CAMPAIGN_STATUS,
  getCampaignDescription,
  getCampaignImage,
  getCampaignStatus,
  getCampaignTitle,
  getCampaignUuid,
  getReservationExpiry,
} from "./utils";
import pageStyles from "./pageStyles";
import Footer1 from "../../components/Footer/Footer";
import AppBar from "../../components/AppBar";
import { useGetNavItems } from "../Home/appbarNav";

const TicketHistoryRow = ({ ticket }) => {
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const campaign = ticket.campaign || ticket;
  const campaignUuid = getCampaignUuid(campaign) || ticket.campaign_uuid;
  const ticketUuid = ticket.uuid || ticket.ticket_uuid || ticket.ticketUuid;
  const campaignForDisplay = {
    ...campaign,
    status: ticket.status || campaign.status,
    reserved: ticket.reserved,
    booked: ticket.booked,
    is_reserved: ticket.is_reserved,
    is_booked: ticket.is_booked,
    reservation_expires_at: ticket.reservation_expires_at || campaign.reservation_expires_at,
  };
  const status = getCampaignStatus(campaignForDisplay);
  const expiresAt = getReservationExpiry(ticket);
  const ticketCode =
    ticket.ticket_code ||
    ticket.coupon_code ||
    ticket.booking_id ||
    ticket.ticket_id ||
    ticket.uuid;
  const image = getCampaignImage(campaign);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const openTicketFile = (file) => {
    if (file?.url) {
      window.open(file.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (file?.blob) {
      const fileBlob = file.blob.type ? file.blob : new Blob([file.blob], { type: file.contentType });
      const url = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename || "campaign-ticket.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return;
    }

    throw new Error("Ticket file was not returned by the backend.");
  };

  // const onDownloadTicket = async () => {
  //   if (!pdfUrl) {
  //     toast.error("Ticket PDF is not ready yet.");
  //     return;
  //   }

  //   window.open(pdfUrl, "_blank", "noopener,noreferrer");
  // };

  const onDownloadTicket = async () => {
    if (!ticketUuid) {
      toast.error("Ticket is not ready for download yet.");
      return;
    }

    try {
      setIsDownloading(true);
      const file = await downloadCampaignTicket({
        ticketUuid,
        token: userDetails?.token,
      });
      openTicketFile(file);
    } catch (error) {
      toast.error(error?.message || "Unable to download ticket.");
    } finally {
      setIsDownloading(false);
    }
  };

  const onEmailTicket = async () => {
    if (!ticketUuid) {
      toast.error("Ticket is not ready to email yet.");
      return;
    }

    try {
      setIsEmailing(true);
      const result = await sendCampaignTicketEmail({
        ticketUuid,
        token: userDetails?.token,
      });
      toast.success(result?.message || "Ticket email sent.");
    } catch (error) {
      toast.error(error?.message || "Unable to email ticket.");
    } finally {
      setIsEmailing(false);
    }
  };

  return (
    <article css={pageStyles.ticketRow}>
      {image ? (
        <button
          type="button"
          css={pageStyles.ticketImageButton}
          onClick={() => campaignUuid && navigate(`/campaigns/${campaignUuid}`)}
          disabled={!campaignUuid}
          aria-label={`Open ${getCampaignTitle(campaign)} campaign`}
        >
          <img src={image} alt={getCampaignTitle(campaign)} css={pageStyles.ticketImage} />
        </button>
      ) : (
        <div css={pageStyles.ticketImageFallback}>Ticket</div>
      )}
      <div>
        <div css={pageStyles.ticketTitleRow}>
          <h3 css={pageStyles.ticketTitle}>{getCampaignTitle(campaign)}</h3>
          <CampaignStatusBadge status={status} />
        </div>
        <div
          css={pageStyles.ticketDescription}
          dangerouslySetInnerHTML={{ __html: getCampaignDescription(campaign) }}
        />
        <div css={pageStyles.ticketMeta}>
          {ticketCode && (
            <span css={pageStyles.ticketMetaItem}>
              <ConfirmationNumberOutlinedIcon />
              Ticket Id: {ticketCode}
            </span>
          )}
          {campaign.venue && (
            <span css={pageStyles.ticketMetaItem}>
              <PlaceOutlinedIcon />
              Venue: {campaign.venue}
            </span>
          )}
          {status === CAMPAIGN_STATUS.RESERVED && expiresAt && (
            <ReservationCountdown expiresAt={expiresAt} />
          )}
        </div>
      </div>
      <div css={pageStyles.ticketActions}>
        <Button
          css={pageStyles.actionButton}
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          onClick={onDownloadTicket}
          disabled={!ticketUuid || isDownloading}
        >
          {isDownloading ? "Downloading..." : "Download Ticket"}
        </Button>
        <Button
          css={pageStyles.actionButton}
          variant="outlined"
          startIcon={<EmailOutlinedIcon />}
          onClick={onEmailTicket}
          disabled={!ticketUuid || isEmailing}
        >
          {isEmailing ? "Sending..." : "Email Ticket"}
        </Button>
      </div>
    </article>
  );
};

const TicketHistory = () => {
  const navigate = useNavigate();
  const { userDetails } = useContext(UserContext);
  const { data, isLoading, isError } = useTicketHistory({ token: userDetails?.token });
  const tickets = getCollection(data);

  return (
    <>
      <AppBar navItems={useGetNavItems({}).navItems} pageTitle="My Bookings" />
      <main css={pageStyles.page}>
        <div css={pageStyles.container}>
          <header css={pageStyles.header}>
            <div>
              <p css={pageStyles.eyebrow}>Campaign Bookings</p>
              <h1 css={pageStyles.title}>My Bookings</h1>
              <p css={pageStyles.subtitle}>
                Track pending, booked, and expired campaign tickets across all campaigns.
              </p>
            </div>
            <Button
              css={pageStyles.backButton}
              startIcon={<ArrowBackIcon />}
              variant="outlined"
              onClick={() => navigate("/home")}
            >
              Back to Home
            </Button>
          </header>

          {isLoading && (
            <div css={pageStyles.state}>
              <CircularProgress size={26} />
            </div>
          )}
          {isError && <div css={pageStyles.state}>Unable to load ticket history.</div>}
          {!isLoading && !isError && tickets.length === 0 && (
            <div css={pageStyles.state}>No campaign tickets have been booked yet.</div>
          )}
          {!isLoading && !isError && tickets.length > 0 && (
            <section css={pageStyles.ticketList}>
              {tickets.map((ticket, index) => (
                <TicketHistoryRow
                  key={ticket.uuid || ticket.ticket_uuid || ticket.id || index}
                  ticket={ticket}
                />
              ))}
            </section>
          )}
        </div>
      </main>
      <Footer1 />
    </>
  );
};

export default TicketHistory;
